import { z } from 'zod';

export const citationSchema = z.object({
  n: z.number().int(),
  documentId: z.string(),
  title: z.string(),
  publishDate: z.string(),
  page: z.number().int(),
});
export type Citation = z.infer<typeof citationSchema>;

/** Figures straight from OCBC's tables, shown beside the answer. `cite` is the citation number of their source. */
export const panelSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('forecast'),
    cite: z.number().int(),
    instrument: z.string(),
    rows: z.array(z.object({ period: z.string(), value: z.number() })),
    /** OCBC's own caveat printed under its FX table (the figures are a trajectory, not quarter-end targets). */
    note: z.string().nullish(),
  }),
  z.object({
    kind: z.literal('technical'),
    cite: z.number().int(),
    instrument: z.string(),
    asOf: z.string(),
    levels: z.array(z.object({ level: z.string(), value: z.number() })),
  }),
  z.object({
    kind: z.literal('house_view'),
    cite: z.number().int(),
    subject: z.string(),
    view: z.string(),
    tradingView: z.string().nullable(),
    date: z.string(),
  }),
]);
export type Panel = z.infer<typeof panelSchema>;

export const answerSchema = z.object({
  status: z.enum(['answered', 'refused']),
  route: z.enum([
    'forecast',
    'technical',
    'house_view',
    'retrieval',
    'mixed',
    'chat',
    'out_of_scope',
    'not_found',
    'unavailable',
  ]),
  text: z.string(),
  asOf: z.string().nullable(),
  citations: z.array(citationSchema),
  viewChanged: z
    .object({
      instrument: z.string(),
      periodLabel: z.string(),
      from: z.number(),
      to: z.number(),
      fromDate: z.string(),
      toDate: z.string(),
    })
    .nullable(),
  /** On refusals only: questions the research can answer instead. */
  followUps: z.array(z.string()),
  /** On refusals only: why, in one line (how far the research goes, what is out of scope). */
  hint: z.string().nullish(),
  panels: z.array(panelSchema).default([]),
  latencyMs: z.number(),
  /** True when a second model call confirmed each sentence against the source it cites. */
  verified: z.boolean().optional(),
});
export type Answer = z.infer<typeof answerSchema>;

/** Step ids are open-ended: the agent adds one per lookup (lookup-1, lookup-2, ...). */
export const stepSchema = z.object({
  id: z.string().min(1).max(40),
  label: z.string(),
  status: z.enum(['active', 'done', 'skipped']),
  detail: z.string().optional(),
});
export type AgentStep = z.infer<typeof stepSchema>;

/** Server-Sent Events from POST /ask: step updates, answer text deltas, then the final answer. */
export const askEventSchema = z.discriminatedUnion('event', [
  z.object({ event: z.literal('step'), data: stepSchema }),
  z.object({ event: z.literal('delta'), data: z.object({ text: z.string() }) }),
  /** What streamed so far was the model working towards a lookup, not the answer: clear it. */
  z.object({ event: z.literal('reset'), data: z.object({}) }),
  z.object({ event: z.literal('answer'), data: answerSchema }),
  z.object({ event: z.literal('error'), data: z.object({ message: z.string() }) }),
]);
export type AskEvent = z.infer<typeof askEventSchema>;
